import { useState, type CSSProperties } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  ClipboardCheck,
  ClipboardList,
  FileText,
  History,
  Home,
  Library,
  Menu,
  ReceiptText,
  Settings,
  ShieldCheck,
  Stethoscope,
  Users,
  X,
} from 'lucide-react';
import { useAuth } from '../lib/auth';
import { authDriver, devUserEmail } from '../lib/api';
import { hasAnyRole, type RoleCode } from '../lib/types';
import { ROLE_ACCESS } from '../lib/role-access';
import { Button, Loading, Notice } from './ui';
import neuroBackground from '../assets/fondo-neuro.png';

const DEV_USERS = [
  ['admin@ticolweb.local', 'Administrador'],
  ['coordinacion@ticolweb.local', 'Coordinación'],
  ['recepcion@ticolweb.local', 'Recepción'],
  ['tecnica1@ticolweb.local', 'Técnica 1'],
  ['tecnica2@ticolweb.local', 'Técnica 2'],
  ['tecnica3@ticolweb.local', 'Técnica 3'],
  ['tecnica4@ticolweb.local', 'Técnica 4'],
  ['medico@ticolweb.local', 'Médico neurólogo'],
  ['facturacion@ticolweb.local', 'Facturación'],
] as const;

const links: Array<{ to: string; label: string; icon: typeof Home; roles: RoleCode[] }> = [
  { to: '/', label: 'Inicio', icon: Home, roles: ['RECEPCION', 'TECNICA', 'MEDICO', 'FACTURACION', 'COORDINACION', 'ADMINISTRADOR', 'AUDITOR', 'SUPERVISOR', 'INVESTIGACION'] },
  { to: '/patients', label: 'Pacientes', icon: Users, roles: ['RECEPCION', 'TECNICA', 'MEDICO', 'COORDINACION', 'ADMINISTRADOR', 'AUDITOR', 'SUPERVISOR', 'INVESTIGACION'] },
  { to: '/studies', label: 'Estudios', icon: Stethoscope, roles: ['RECEPCION', 'TECNICA', 'MEDICO', 'FACTURACION', 'COORDINACION', 'ADMINISTRADOR', 'AUDITOR', 'SUPERVISOR', 'INVESTIGACION'] },
  { to: '/work/technical', label: 'Captura técnica', icon: Activity, roles: ROLE_ACCESS.technical },
  { to: '/work/medical', label: 'Revisión médica', icon: ClipboardList, roles: ROLE_ACCESS.medical },
  { to: '/clinical-templates', label: 'Plantillas clínicas', icon: Library, roles: ROLE_ACCESS.clinicalTemplates },
  { to: '/work/documents', label: 'Entrega documental', icon: FileText, roles: ROLE_ACCESS.documents },
  { to: '/work/billing', label: 'Facturación', icon: ReceiptText, roles: ROLE_ACCESS.billing },
  { to: '/history', label: 'Historial', icon: History, roles: ['RECEPCION', 'TECNICA', 'MEDICO', 'FACTURACION', 'COORDINACION', 'ADMINISTRADOR', 'AUDITOR', 'SUPERVISOR', 'INVESTIGACION'] },
];

export function AppShell() {
  const { user, loading, error, driver, login, logout, switchDevUser } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const loginStyle = { '--login-bg': `url(${neuroBackground})` } as CSSProperties;

  if (loading) return <div className="full-center"><Loading label="Validando acceso…" /></div>;
  if (!user) {
    return (
      <div className="login-panel neuro-login" style={loginStyle}>
        <div className="login-shell">
          <section className="login-intro" aria-label="Gestión Consultorio">
            <span className="login-eyebrow">Neurología operativa</span>
            <h1>Gestión Consultorio</h1>
          </section>
          <div className="login-card">
            <div className="login-card-head">
              <div className="brand-mark login-brand-mark"><Activity size={22} aria-hidden="true" /></div>
              <div>
                <strong>Acceso seguro</strong>
                <small>Gestión Consultorio · Neurodiagnóstico</small>
              </div>
            </div>
            <h2>Ingreso a la plataforma</h2>
            <p>Use su identidad autorizada para continuar.</p>
            {error && <Notice kind="error">{error}</Notice>}
            {driver === 'entra' ? (
              <Button onClick={() => void login()}><ShieldCheck size={17} /> Ingresar con Microsoft</Button>
            ) : (
              <div className="stack login-local-panel">
                <p className="muted">Modo local. Seleccione una identidad individual de prueba.</p>
                <select defaultValue={devUserEmail()} onChange={(event) => void switchDevUser(event.target.value)}>
                  {DEV_USERS.map(([email, label]) => <option key={email} value={email}>{label} · {email}</option>)}
                </select>
                <Button onClick={() => void switchDevUser(devUserEmail())}>Ingresar</Button>
              </div>
            )}
            <small className="login-security-note">Sistema privado para usuarios autorizados.</small>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <button className="mobile-menu" onClick={() => setOpen(!open)} aria-label="Abrir menú">{open ? <X /> : <Menu />}</button>
      <aside className={open ? 'sidebar open' : 'sidebar'}>
        <div className="brand"><span><Activity size={20} aria-hidden="true" /></span><div><strong>Gestión Consultorio</strong><small>Neurodiagnóstico</small></div></div>
        <nav>
          {links.filter((link) => hasAnyRole(user, link.roles)).map(({ to, label, icon: Icon }) => (
            <NavLink key={to} to={to} end={to === '/'} onClick={() => setOpen(false)}><Icon size={19} /><span>{label}</span></NavLink>
          ))}
          {hasAnyRole(user, ROLE_ACCESS.admin) && (
            <NavLink to="/admin" onClick={() => setOpen(false)}><Settings size={19} /><span>Administración</span></NavLink>
          )}
        </nav>
        <div className="sidebar-user">
          <div className="avatar">{user.displayName.charAt(0)}</div>
          <div className="user-copy"><strong>{user.displayName}</strong><small>{user.roles.join(' · ').replaceAll('_', ' ')}</small></div>
          <button title="Cerrar sesión" onClick={() => void logout()}>↪</button>
        </div>
        {authDriver() === 'dev' && (
          <div className="dev-switcher">
            <label htmlFor="dev-user">Cambiar perfil local</label>
            <select id="dev-user" value={user.email} onChange={(event) => void switchDevUser(event.target.value)}>
              {DEV_USERS.map(([email, label]) => <option key={email} value={email}>{label}</option>)}
            </select>
          </div>
        )}
      </aside>
      <main className="main-content" key={location.pathname}><Outlet /></main>
    </div>
  );
}
